
import { Button } from "@/components/ui/button";
import { useTranslatedText } from "@/hooks/useTranslate";
import { useNavigate } from "react-router-dom";
import { ChevronDown, MapPin } from "lucide-react";
import { useEffect, useState, useRef } from "react";

const AnimatedCounter = ({ target, suffix = "" }: { target: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [started, target]);

  return (
    <div ref={ref} className="text-4xl lg:text-5xl font-bold text-white">
      +{count}{suffix}
    </div>
  );
};

const TypewriterText = ({ words }: { words: string[] }) => {
  const [currentWord, setCurrentWord] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[currentWord];
    const timeout = deleting ? 50 : 100;

    const timer = setTimeout(() => {
      if (!deleting) {
        setDisplayed(word.slice(0, displayed.length + 1));
        if (displayed.length + 1 === word.length) {
          setTimeout(() => setDeleting(true), 1500);
        }
      } else {
        setDisplayed(word.slice(0, displayed.length - 1));
        if (displayed.length === 0) {
          setDeleting(false);
          setCurrentWord((prev) => (prev + 1) % words.length);
        }
      }
    }, timeout);

    return () => clearTimeout(timer);
  }, [displayed, deleting, currentWord, words]);

  return (
    <span className="bg-gradient-to-r from-techno-purple to-techno-accent bg-clip-text text-transparent">
      {displayed}<span className="animate-pulse text-techno-purple">|</span>
    </span>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();

  const titleText = useTranslatedText("Transformamos ideas en");
  const subtitleText = useTranslatedText("Creamos experiencias digitales extraordinarias que impulsan el crecimiento de tu negocio a nivel mundial.");
  const conocenosText = useTranslatedText("Conócenos");
  const verProyectosText = useTranslatedText("Ver proyectos");
  const badgeText = useTranslatedText("Desde República Dominicana para el mundo");
  const proyectosText = useTranslatedText("Proyectos");
  const clientesText = useTranslatedText("Clientes");
  const paisesText = useTranslatedText("Países");

  const typewriterWords = ["éxito digital", "innovación", "resultados", "futuro web"];

  return (
    <section id="inicio" className="min-h-screen bg-hero-gradient relative overflow-hidden flex items-center">
      {/* Animated grid background */}
      <div className="absolute inset-0 hero-grid"></div>

      {/* Floating orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/6 w-72 h-72 bg-techno-purple/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-techno-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/2 right-1/6 w-48 h-48 bg-techno-gradient/15 rounded-full blur-2xl animate-float" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        {/* Badge */}
        <div className="flex justify-center mb-8 animate-fade-in">
          <div className="glass rounded-full px-6 py-2 flex items-center gap-2 text-sm text-white/90">
            <MapPin className="w-4 h-4 text-techno-accent" />
            {badgeText}
          </div>
        </div>

        {/* Main content centered */}
        <div className="text-center max-w-5xl mx-auto space-y-8">
          <h1 className="text-5xl lg:text-7xl xl:text-8xl font-bold text-white leading-tight">
            {titleText}{" "}
            <br />
            <TypewriterText words={typewriterWords} />
          </h1>

          <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {subtitleText}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <Button
              size="lg"
              className="bg-button-gradient hover:opacity-90 transition-all duration-300 pulse-glow text-lg px-10 py-6 rounded-xl"
              onClick={() => navigate('/nosotros')}
            >
              {conocenosText}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-techno-purple/50 text-white hover:bg-techno-purple/20 transition-all duration-300 text-lg px-10 py-6 rounded-xl backdrop-blur-sm"
              onClick={() => navigate('/proyectos')}
            >
              {verProyectosText}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto pt-12 animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <div className="text-center space-y-2">
              <AnimatedCounter target={200} />
              <p className="text-gray-400 text-sm uppercase tracking-wider">{proyectosText}</p>
            </div>
            <div className="text-center space-y-2 border-x border-white/10">
              <AnimatedCounter target={50} />
              <p className="text-gray-400 text-sm uppercase tracking-wider">{clientesText}</p>
            </div>
            <div className="text-center space-y-2">
              <AnimatedCounter target={5} />
              <p className="text-gray-400 text-sm uppercase tracking-wider">{paisesText}</p>
            </div>
          </div>
        </div>

        {/* Scroll arrow */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <ChevronDown className="w-8 h-8 text-white/50" style={{ animation: 'bounce-arrow 1.5s ease-in-out infinite' }} />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
